import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Button,
  Chip
} from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import InventoryIcon from '@mui/icons-material/Inventory'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import ReceiptIcon from '@mui/icons-material/Receipt'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import ListAltIcon from '@mui/icons-material/ListAlt'
import WarningIcon from '@mui/icons-material/Warning'
import ErrorIcon from '@mui/icons-material/Error'
import { useNavigate } from 'react-router-dom'
import { dcApi } from '../services/dc.api'

const orderStatusColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  confirmed: 'primary',
  ordered: 'info',
  partially_received: 'warning',
  received: 'secondary',
  allocated: 'success',
  completed: 'success'
}

const formatCurrency = (value: number) =>
  'Rs. ' + (value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })

export default function DCDashboard() {
  const navigate = useNavigate()

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dc-stats'],
    queryFn: dcApi.getStats
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load dashboard data. Please try again later.
      </Alert>
    )
  }

  const uploadStats = stats?.uploads || { total_uploads: 0, processed_uploads: 0, failed_uploads: 0, total_items: 0, matched_items: 0 }
  const comparisonStats = stats?.comparisons || { items_with_comparison: 0, avg_best_discount: 0, avg_offers_per_item: 0 }
  const distributorBreakdown = stats?.distributorBreakdown || []
  const ordering = stats?.ordering || { managed_items: 0, total_orders: 0, draft_orders: 0, confirmed_orders: 0, ordered_count: 0, in_progress_orders: 0, total_order_value: 0, recent_orders: [] }
  const purchases = stats?.purchases || { total_purchases: 0, draft_purchases: 0, confirmed_purchases: 0, inventoried_purchases: 0, total_purchase_value: 0 }
  const inventory = stats?.inventory || { total_items: 0, total_medicines: 0, total_available_qty: 0, total_value: 0, expiring_30d: 0, expired: 0 }
  const transfers = stats?.transfers || { total_transfers: 0, draft_transfers: 0, confirmed_transfers: 0, completed_transfers: 0, total_transfer_value: 0 }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* Quick Actions */}
      <Box mb={4}>
        <Grid container spacing={2}>
          <Grid item>
            <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => navigate('/upload')} size="large">
              Upload New File
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<CompareArrowsIcon />} onClick={() => navigate('/comparison')} size="large">
              View Comparison
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<ShoppingCartIcon />} onClick={() => navigate('/orders/create')} size="large">
              Create Order
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<ReceiptIcon />} onClick={() => navigate('/purchases/upload')} size="large">
              Upload Purchase
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" startIcon={<WarehouseIcon />} onClick={() => navigate('/inventory')} size="large">
              View Inventory
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Price Comparison Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <CloudUploadIcon color="primary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">Total Uploads</Typography>
              </Box>
              <Typography variant="h4">{uploadStats.total_uploads || 0}</Typography>
              <Typography variant="body2" color="success.main">
                {uploadStats.processed_uploads || 0} processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <InventoryIcon color="secondary" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">Total Items</Typography>
              </Box>
              <Typography variant="h4">{uploadStats.total_items || 0}</Typography>
              <Typography variant="body2" color="success.main">
                {uploadStats.matched_items || 0} matched
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <CompareArrowsIcon color="info" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">Items Compared</Typography>
              </Box>
              <Typography variant="h4">{comparisonStats.items_with_comparison || 0}</Typography>
              <Typography variant="body2" color="textSecondary">
                Avg {(comparisonStats.avg_offers_per_item || 0).toFixed(1)} offers/item
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingDownIcon color="success" sx={{ mr: 1 }} />
                <Typography color="textSecondary" variant="body2">Avg Best Discount</Typography>
              </Box>
              <Typography variant="h4">
                {(comparisonStats.avg_best_discount || 0).toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">Effective discount</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DC Ordering Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">DC Centralized Ordering</Typography>
          <Button size="small" onClick={() => navigate('/orders')}>View All Orders</Button>
        </Box>
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ListAltIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">Managed Items</Typography>
                </Box>
                <Typography variant="h4">{ordering.managed_items}</Typography>
                <Typography variant="body2" color="textSecondary">
                  <Button size="small" sx={{ p: 0, minWidth: 0 }} onClick={() => navigate('/orders/managed')}>
                    View managed list
                  </Button>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShoppingCartIcon color="info" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">Total Orders</Typography>
                </Box>
                <Typography variant="h4">{ordering.total_orders}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatCurrency(ordering.total_order_value)} total value
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <ShoppingCartIcon color="warning" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">Pending Action</Typography>
                </Box>
                <Typography variant="h4">
                  {ordering.draft_orders + ordering.confirmed_orders + ordering.in_progress_orders}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {ordering.draft_orders} draft, {ordering.confirmed_orders} confirmed, {ordering.in_progress_orders} in progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Orders Table */}
        {ordering.recent_orders.length > 0 && (
          <>
            <Typography variant="subtitle2" color="textSecondary" mb={1}>Recent Orders</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Items</TableCell>
                    <TableCell align="right">Est. Cost</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordering.recent_orders.map((order) => (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <TableCell>{order.order_number}</TableCell>
                      <TableCell>
                        <Chip
                          label={order.status.replace('_', ' ')}
                          size="small"
                          color={orderStatusColors[order.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">{order.total_items}</TableCell>
                      <TableCell align="right">{formatCurrency(order.total_estimated_cost || 0)}</TableCell>
                      <TableCell>
                        {order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* Purchases & Inventory Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Purchases & Inventory</Typography>
          <Box>
            <Button size="small" onClick={() => navigate('/purchases')} sx={{ mr: 1 }}>Purchases</Button>
            <Button size="small" onClick={() => navigate('/inventory')}>Inventory</Button>
          </Box>
        </Box>

        {purchases.total_purchases === 0 && inventory.total_items === 0 ? (
          <Alert severity="info">
            No purchase bills uploaded yet. Upload a purchase CSV to start tracking DC inventory.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" variant="body2">Total Purchases</Typography>
                  </Box>
                  <Typography variant="h4">{purchases.total_purchases}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {formatCurrency(purchases.total_purchase_value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <WarehouseIcon color="secondary" sx={{ mr: 1 }} />
                    <Typography color="textSecondary" variant="body2">DC Inventory</Typography>
                  </Box>
                  <Typography variant="h4">{inventory.total_medicines}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    medicines, {inventory.total_available_qty} qty, {formatCurrency(inventory.total_value)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <WarningIcon color={inventory.expiring_30d > 0 ? 'warning' : 'disabled'} sx={{ mr: 1 }} />
                    <Typography color="textSecondary" variant="body2">Expiring Soon</Typography>
                  </Box>
                  <Typography variant="h4" color={inventory.expiring_30d > 0 ? 'warning.main' : 'inherit'}>
                    {inventory.expiring_30d}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Within 30 days</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <ErrorIcon color={inventory.expired > 0 ? 'error' : 'disabled'} sx={{ mr: 1 }} />
                    <Typography color="textSecondary" variant="body2">Expired</Typography>
                  </Box>
                  <Typography variant="h4" color={inventory.expired > 0 ? 'error.main' : 'inherit'}>
                    {inventory.expired}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">Past expiry date</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Store Transfers Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Store Transfers</Typography>
          <Button size="small" onClick={() => navigate('/transfers')}>View All Transfers</Button>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <LocalShippingIcon color="primary" sx={{ mr: 1 }} />
                  <Typography color="textSecondary" variant="body2">Total Transfers</Typography>
                </Box>
                <Typography variant="h4">{transfers.total_transfers}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatCurrency(transfers.total_transfer_value)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" variant="body2" mb={1}>Draft</Typography>
                <Typography variant="h4">{transfers.draft_transfers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" variant="body2" mb={1}>Confirmed</Typography>
                <Typography variant="h4">{transfers.confirmed_transfers}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography color="textSecondary" variant="body2" mb={1}>Completed</Typography>
                <Typography variant="h4" color="success.main">{transfers.completed_transfers}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Enhanced Distributor Breakdown */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Items by Distributor
        </Typography>
        {distributorBreakdown.length === 0 ? (
          <Typography color="textSecondary">
            No data available. Upload distributor price lists to see comparison.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Distributor</TableCell>
                  <TableCell align="right">Item Count</TableCell>
                  <TableCell align="right">Avg Discount %</TableCell>
                  <TableCell align="right">Min Landing Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributorBreakdown.map((row) => (
                  <TableRow key={row.name}>
                    <TableCell>{row.name || 'Unknown'}</TableCell>
                    <TableCell align="right">{row.item_count}</TableCell>
                    <TableCell align="right">
                      {(Number(row.avg_discount) || 0).toFixed(1)}%
                    </TableCell>
                    <TableCell align="right">
                      {Number(row.min_landing_cost) > 0
                        ? formatCurrency(Number(row.min_landing_cost))
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}
